import type { OnboardingStateAnswer } from '../../../../music24-api/src/trpc/onboarding.router';

/**
 * The page that a person asked for when the welcome page came in front of it (QA 2026-10-24 B02):
 * a deep link of an email, or the `returnTo` page of a sign-in. After the welcome, the app opens it.
 * It lives in sessionStorage (one tab) and in memory, because the storage can fail. The id of the
 * user goes with it: a second person who signs in on the same tab never gets the page of the first.
 */
const KEY = 'm24WelcomeReturnTo';

interface SavedTarget {
  userId: number;
  target: string;
}

let memoryTarget: SavedTarget | null = null;

/** Reads a stored value. An old plain string (no user id) or a bad value gives null. Pure. */
export function parseSavedTarget(raw: string | null): SavedTarget | null {
  if (raw === null) {
    return null;
  }
  try {
    const value: unknown = JSON.parse(raw);
    if (
      value !== null &&
      typeof value === 'object' &&
      typeof (value as SavedTarget).userId === 'number' &&
      typeof (value as SavedTarget).target === 'string'
    ) {
      return { userId: (value as SavedTarget).userId, target: (value as SavedTarget).target };
    }
  } catch {
    // Not JSON: an old value with no user id.
  }
  return null;
}

/** The start pages: they open Home anyway, so they need no saved target. */
const START_PATHS = new Set(['', 'home']);

/** The path relative to the router basename `/app`, with no slash at the start or the end. Pure. */
export function welcomePathOf(pathname: string): string {
  return pathname.replace(/^\/+/, '').replace(/\/+$/, '');
}

/** True for Home and the root of the app. Pure. */
export function isWelcomeStartPage(pathname: string): boolean {
  return START_PATHS.has(welcomePathOf(pathname));
}

/**
 * True for an account that already has an artist with a song, or a member of a team workspace. Such
 * a person sees the welcome page one time on Home, but a deep link opens at once. Pure.
 */
export function isEstablishedAccount(
  state: Pick<OnboardingStateAnswer, 'isMember' | 'artist' | 'firstReleaseUuid'>
): boolean {
  if (state.isMember) {
    return true;
  }
  return state.artist !== null && state.firstReleaseUuid !== null;
}

/** Keeps the asked page of `userId`. `target` is relative to `/app`, with its query. */
export function saveWelcomeReturnTarget(userId: number | null, pathname: string, search: string): void {
  if (userId === null) {
    return;
  }
  const path = welcomePathOf(pathname);
  if (START_PATHS.has(path)) {
    return;
  }
  const saved: SavedTarget = { userId, target: `${path}${search}` };
  memoryTarget = saved;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(saved));
  } catch {
    // Blocked storage: the memory copy lasts for this page view.
  }
}

/** True when a saved page waits, of any user (`take` drops the page of another user). */
export function hasWelcomeReturnTarget(): boolean {
  try {
    if (window.sessionStorage.getItem(KEY) !== null) {
      return true;
    }
  } catch {
    // The memory copy decides.
  }
  return memoryTarget !== null;
}

/**
 * The saved page of `userId`, once: the call clears the saved page of every user. Null when none
 * waits, or when the saved page belongs to another user.
 */
export function takeWelcomeReturnTarget(userId: number | null): string | null {
  let saved = memoryTarget;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (raw !== null) {
      saved = parseSavedTarget(raw);
    }
    window.sessionStorage.removeItem(KEY);
  } catch {
    // The memory copy decides.
  }
  memoryTarget = null;
  if (saved === null || userId === null || saved.userId !== userId) {
    return null;
  }
  return saved.target;
}
